/**
 * YetkiYonetimiPage.jsx — Sistem Ayarları › Yetki Yönetimi (backlog #6, 2026-10-08)
 * Route: /Management/SistemAyarlari/Yetkiler
 * Roller: [1] — YALNIZCA kök kullanıcı (SuperAdmin). Backend (RbacController) her çağrıda aynı
 * kontrolü veritabanından yapar (oturum yoksa 401, kök değilse 403); rota kısıtı yalnızca ilk kapı.
 *
 * Üç bölüm:
 *  1. Roller ve Yetkiler — rol × yetki matrisi. Satır başına Kaydet: PUT /rbac/groups/{id}/permissions
 *     rolün TAM kümesini yazar. SuperAdmin satırı salt okunur (kodda her yetkiye sahip). Rol adı
 *     kalemle değişir. "Yeni Rol" bir PERSONEL rolünü şablon alır — firma tarafı rol yaratılamaz,
 *     çünkü tenancy (UserGroupConstant) firma girişlerini sabit 9/19 id'leriyle tanıyor.
 *  2. Rol Üyeleri — seçili rolün kullanıcıları (aramalı, ilk 200), satırdan başka role taşıma
 *     (userService.updateUserGroup → PUT /users/{id}/group). SuperAdmin hedef olarak sunulmaz:
 *     backend artık kimseyi rol değişikliğiyle kök yapmıyor.
 *  3. Kök Devri — kökü başka bir aktif personele devreder, siz seçtiğiniz personel rolüne geçersiniz
 *     (tek işlem). Onay için "DEVRET" yazılır. Devirden sonra bu sayfa size kapanır.
 *
 * Sayfa erişimi (backlog #302): sayfalar ve menü hâlâ sabit rol id'leriyle açılıyor (allRoutes.js
 * `roles`, MainContext api.groups), ama her personel rolü artık "şu rolün sayfaları" diyebilir —
 * PUT /rbac/groups/{id}/page-access, user_group.page_access_group_id (V70). Yeni rol şablonunun
 * sayfalarıyla doğar; satırdaki "Sayfa erişimi" seçimi rolün diğer değişiklikleriyle birlikte
 * Kaydet'le yazılır. Kullanıcılar değişikliği bir sonraki girişlerinde görür (groupInfo önbelleği).
 * WAS: "⚠️ Yeni bir rolün kullanıcıları, Routes/allRoutes.js'teki sabit `roles` dizileri o rolün
 *    id'sini içerene kadar korumalı sayfaları açamaz: menü varsayılana düşer, sayfa "yetkiniz yok" der."
 * #325 (2026-10-11): yukarıdaki #302 ödünç alma bitti. Sayfalar ve menü artık SAYFA YETKİLERİYLE açılıyor
 * (page.*, matristeki "Sayfa:" sütunları; V76 her rolün eski sayfalarını birebir verdi). Yeni rol şablonunun
 * sayfa yetkilerini kopyalayarak doğar; "Sayfa erişimi" sütunu artık yalnızca bilgi gösterir.
 * #408 (2026-10-14): o bilgi sütunu, kaydetme dalı ve PUT /rbac/groups/{id}/page-access çağrısı KALDIRILDI (aşağıda "WAS" olarak
 * yorumda): user_group.page_access_group_id sütunu ve endpoint'i backend'den silindi (V94). Sayfalar "Sayfa:" yetki sütunlarıyla açılır.
 *
 * #309 (2026-10-10, Bilge) — YÖNETİM SAYFASI. Artık sekmeli:
 *  Kullanıcılar (ayrıntılı süzgeç, rol değiştirme — personel yapma / personellikten çıkarma dahil —,
 *  firma bağlantısı), Roller ve Yetkiler (yukarıdaki 1 + 2), Firma–Kullanıcı, İşlem Geçmişi, Kök Devri.
 *  Erişim rol id'siyle değil YETKİYLE: rbac.manage (kök her zaman sahip; başka role yalnızca kök verir).
 *  Rota `permission: "rbac.manage"`, menü GET /rbac/me'den. Kök olmayan yönetici kendi rolünü ve
 *  yetkilerini değiştiremez, rbac.manage'i veremez — backend 403 döner, burada da kilitli gösterilir.
 *  Yetkilerin bir TARAFI var (permission.kapsam, V72): personel yetkileri yalnızca personel rollerine,
 *  firma portalı yetkileri (portal.*) yalnızca firma tarafı rollere (Firma Moderatör/Admin,
 *  Organizatör rolleri, ...) verilir; karşı taraftaki hücre "—".
 *
 *  #419 (2026-10-14, tasarım #409 D12): firma portalı yetkileri (7-15) EMEKLİ. Backend `retired` işaretler, bu sayfa o sütunları
 *  göstermez; firma tarafı rollerin hücreleri artık hep "—" (portaldaki yetkiler Portal Rolleri'nde / Firma Detay › Portal
 *  Kullanıcıları'nda verilir). Eski satırlar silinmedi, hiçbir şey okumaz.
 *  #421: rol adları ve yetki adları isteğin dilinde gelir (İngilizce yoksa Türkçe); rol oluştururken ve adını değiştirirken
 *  İngilizce ad da yazılır (userGroupNameEn, isteğe bağlı; boş = yok).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container, Card, CardBody, CardHeader, Row, Col, Button, Alert, Spinner, Input, Label, Table,
  Badge, Modal, ModalHeader, ModalBody, ModalFooter, UncontrolledTooltip, Nav, NavItem, NavLink,
} from "reactstrap";
import { ShieldCheck, Users as UsersIcon, KeyRound, Pencil, Plus, Save, RotateCcw, X, Check, History } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  listPermissions, listRoles, createRole, renameRole, setRolePermissions, listRoleMembers,
  // WAS (until #408): setRolePageAccess,
  transferRoot, getMyPermissions, setManagedUserGroup, KAPSAM, PERMISSION_KEYS,
} from "../../../services/rbacService";
// WAS (until #309): import { getStaffUsers, updateUserGroup } from "../../../services/userService";
import { getStaffUsers } from "../../../services/userService";
import YetkiKullanicilarSekmesi from "./YetkiKullanicilarSekmesi";
import YetkiFirmaKullaniciSekmesi from "./YetkiFirmaKullaniciSekmesi";
import YetkiGecmisSekmesi from "./YetkiGecmisSekmesi";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

const TRANSFER_WORD = "DEVRET";

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString(currentLocaleTag());
};
const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));
/** Personel rolü: kök değil, firma tarafı değil, is_denetmen = 1. Şablon ve devir hedefleri bunlar. */
const isStaffRole = (r) => !r.root && !r.firmaSide && r.isDenetmen === 1;

function roleKind(r) {
  if (r.root) return { label: i18n.t("ayarlar:yetkiYonetimi.kok"), color: "danger" };
  if (r.firmaSide) return { label: i18n.t("ayarlar:yetkiYonetimi.firma"), color: "warning" };
  if (r.isDenetmen === 2) return { label: i18n.t("ayarlar:yetkiYonetimi.firmaPaneli"), color: "info" }; // Organizatör, Hekim, Uzman
  return { label: i18n.t("ayarlar:yetkiYonetimi.personel"), color: "success" };
}

// WAS (until #408, the page-access column is gone): /** #302: kayıtlı sayfa erişimi, <select> değeri olarak ("" = kendi sayfaları). */
// const storedPage = (r) => (r.pageAccessGroupId == null ? "" : String(r.pageAccessGroupId));

/** #308: permission.kapsam (eski backend göndermezse personel sayılır). */
const kapsamOf = (p) => (p.kapsam === KAPSAM.PORTAL ? KAPSAM.PORTAL : KAPSAM.PERSONEL);
/** #308: bu yetki bu rolün tarafına mı ait? Personel rolüne personel, firma tarafı role portal yetkisi. */
const sideMatches = (r, p) => (kapsamOf(p) === KAPSAM.PORTAL) !== Boolean(r.staff);

// ─── 1. Roller ve Yetkiler ─────────────────────────────────────────────────────────────
// WAS (until #309): function RollerKarti({ roles, permissions, onReload, onShowMembers }) {
function RollerKarti({ roles, permissions, onReload, onShowMembers, onShowHistory, me }) {
  const { t } = useTranslation("ayarlar");
  const [drafts, setDrafts] = useState({}); // groupId -> permissionIds (sıralı)
  // WAS (until #408): const [pageDrafts, setPageDrafts] = useState({}); // #302: groupId -> "" | "<rol id>"
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [renaming, setRenaming] = useState(null); // { id, value }
  const [newOpen, setNewOpen] = useState(false);

  const current = (r) => drafts[r.userGroupId] ?? r.permissionIds;
  const permsDirty = (r) => Boolean(drafts[r.userGroupId]) && !sameSet(drafts[r.userGroupId], r.permissionIds);
  // WAS (until #408): const currentPage = (r) => pageDrafts[r.userGroupId] ?? storedPage(r);
  // WAS (until #408): const pageDirty = (r) => r.userGroupId in pageDrafts && pageDrafts[r.userGroupId] !== storedPage(r);
  // WAS: const isDirty = (r) => Boolean(drafts[r.userGroupId]) && !sameSet(drafts[r.userGroupId], r.permissionIds);
  // WAS (until #408): const isDirty = (r) => permsDirty(r) || pageDirty(r);
  const isDirty = (r) => permsDirty(r);
  // WAS: const dropDraft = (id) => setDrafts((p) => { const n = { ...p }; delete n[id]; return n; });
  // WAS (until #408): the same plus setPageDrafts((p) => { const n = { ...p }; delete n[id]; return n; });
  const dropDraft = (id) => {
    setDrafts((p) => { const n = { ...p }; delete n[id]; return n; });
  };

  // #302: a page source is a staff role that does not borrow pages itself; a role others borrow from
  // cannot borrow (backend refuses both — no chains).
  // WAS (until #325, the page-access cell is retired):
  // const borrowersOf = (r) => roles.filter((x) => x.pageAccessGroupId === r.userGroupId);
  // const pageSources = (r) => roles.filter((x) => isStaffRole(x) && x.pageAccessGroupId == null && x.userGroupId !== r.userGroupId);
  const roleName = (id) => roles.find((x) => x.userGroupId === Number(id))?.userGroupName ?? `#${id}`;

  const toggle = (r, pid) => setDrafts((p) => {
    const set = new Set(p[r.userGroupId] ?? r.permissionIds);
    if (set.has(pid)) set.delete(pid); else set.add(pid);
    return { ...p, [r.userGroupId]: [...set].sort((a, b) => a - b) };
  });

  async function save(r) {
    setBusy(r.userGroupId);
    setMsg(null);
    try {
      // WAS: await setRolePermissions(r.userGroupId, current(r));
      const saved = [];
      if (permsDirty(r)) {
        await setRolePermissions(r.userGroupId, current(r));
        saved.push("yetkiler");
      }
      // WAS (until #408): the page-access branch of a row's Kaydet:
      // if (pageDirty(r)) {
      //   const v = currentPage(r);
      //   await setRolePageAccess(r.userGroupId, v === "" ? null : Number(v));
      //   saved.push("sayfaErisimi"); // #349: a key, translated where the message is built
      // }
      await onReload();
      dropDraft(r.userGroupId);
      // WAS: text: `${r.userGroupName}: yetkiler kaydedildi.`
      setMsg({
        color: "success",
        // WAS: text: `${r.userGroupName}: ${saved.join(" ve ")} kaydedildi.`
        //   + (saved.includes("sayfa erişimi") ? " Rolün kullanıcıları yeni sayfaları bir sonraki girişlerinde görür." : ""),
        // #349: the saved parts are keys (yetkiler / sayfaErisimi), translated here.
        text: i18n.t("ayarlar:yetkiYonetimi.kaydedildi", {
          rol: r.userGroupName,
          neler: saved.map((k) => i18n.t(`ayarlar:yetkiYonetimi.kaydedilen.${k}`)).join(i18n.t("ayarlar:yetkiYonetimi.ve")),
        }), // WAS (until #408): + (saved.includes("sayfaErisimi") ? i18n.t("ayarlar:yetkiYonetimi.sayfaErisimiNotu") : ""),
      });
    } catch (e) {
      setMsg({ color: "danger", text: e.message });
    } finally {
      setBusy(null);
    }
  }

  async function saveName() {
    const target = renaming;
    setBusy(target.id);
    setMsg(null);
    try {
      // WAS (until #421): await renameRole(target.id, target.value);
      await renameRole(target.id, target.value, target.valueEn);
      await onReload();
      setRenaming(null);
      setMsg({ color: "success", text: t("yetkiYonetimi.rolAdiKaydedildi") });
    } catch (e) {
      setMsg({ color: "danger", text: e.message });
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
        <ShieldCheck size={16} className="text-muted" />
        <h5 className="card-title mb-0 flex-grow-1">{t("yetkiYonetimi.rollerVeYetkiler")}</h5>
        <Button color="primary" size="sm" onClick={() => setNewOpen(true)}>
          <Plus size={14} className="me-1" />{t("yetkiYonetimi.yeniRol")}
        </Button>
      </CardHeader>
      <CardBody className="p-0">
        {msg && <Alert color={msg.color} className="m-3 mb-0" toggle={() => setMsg(null)}>{msg.text}</Alert>}
        <div className="table-responsive">
          <Table className="align-middle table-nowrap mb-0">
            <thead className="table-light">
              <tr>
                <th className="ps-3" style={{ minWidth: 220 }}>{t("yetkiYonetimi.rol")}</th>
                <th style={{ width: 120 }}>{t("yetkiYonetimi.tur")}</th>
                {/* WAS (until #408): the info column "Sayfa erişimi" (an <th> with the tooltip t("yetkiYonetimi.n325SayfalarArtikSayfaYetkileriyle")):
                    the role's pages are the "Sayfa:" permission columns below, and the page-access mechanism it came from is gone. */}
                <th className="text-center" style={{ width: 100 }}>{t("yetkiYonetimi.aktifUye")}</th>
                {permissions.map((p) => (
                  <th key={p.permissionId} className="text-center" style={{ minWidth: 130 }}>
                    {/* #308: hangi tarafın yetkisi */}
                    <Badge color={kapsamOf(p) === KAPSAM.PORTAL ? "warning" : "success"} className="mb-1" style={{ fontSize: 9 }}>
                      {kapsamOf(p) === KAPSAM.PORTAL ? t("yetkiYonetimi.portal") : t("yetkiYonetimi.personel")}
                    </Badge>
                    <br />
                    <span id={`yetki-th-${p.permissionId}`} style={{ cursor: "help" }}>{p.permissionName}</span>
                    <div className="fw-normal text-muted font-monospace" style={{ fontSize: 10 }}>{p.permissionKey}</div>
                    {p.description && (
                      <UncontrolledTooltip target={`yetki-th-${p.permissionId}`} placement="top">{p.description}</UncontrolledTooltip>
                    )}
                  </th>
                ))}
                <th className="text-end pe-3" style={{ width: 150 }}>{t("yetkiYonetimi.islemler")}</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => {
                const kind = roleKind(r);
                const ids = current(r);
                const dirty = isDirty(r);
                const editingName = renaming?.id === r.userGroupId;
                return (
                  <tr key={r.userGroupId} className={dirty ? "table-warning" : undefined}>
                    <td className="ps-3">
                      {editingName ? (
                        <div className="d-flex gap-1 align-items-start">
                          {/* WAS (until #421): a single <Input value={renaming.value} .../> (the Turkish name only) */}
                          <div className="d-flex flex-column gap-1 flex-grow-1">
                            <Input
                              bsSize="sm" value={renaming.value} autoFocus disabled={busy === r.userGroupId}
                              aria-label={t("yetkiYonetimi.rolAdi")} placeholder={t("yetkiYonetimi.rolAdi")}
                              onChange={(e) => setRenaming({ ...renaming, value: e.target.value })}
                              onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setRenaming(null); }}
                            />
                            {/* #421: the English name; blank = none (the Turkish name shows in English) */}
                            <Input
                              bsSize="sm" value={renaming.valueEn} maxLength={255} disabled={busy === r.userGroupId}
                              aria-label={t("yetkiYonetimi.rolAdiEn")} placeholder={t("yetkiYonetimi.rolAdiEnIpucu")}
                              onChange={(e) => setRenaming({ ...renaming, valueEn: e.target.value })}
                              onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setRenaming(null); }}
                            />
                          </div>
                          <Button size="sm" color="success" title={t("common:kaydet")} disabled={!renaming.value.trim() || busy === r.userGroupId} onClick={saveName}>
                            <Check size={14} />
                          </Button>
                          <Button size="sm" color="light" className="border" title={t("yetkiYonetimi.vazgec")} onClick={() => setRenaming(null)}>
                            <X size={14} />
                          </Button>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-semibold">{r.userGroupName}</span>
                          <span className="text-muted small font-monospace">#{r.userGroupId}</span>
                          {!r.root && (
                            <Button
                              size="sm" color="link" className="p-0 text-muted" title={t("yetkiYonetimi.adiniDegistir")}
                              // WAS (until #421): onClick={() => setRenaming({ id: r.userGroupId, value: r.userGroupName ?? "" })}
                              // #421: userGroupName is in the viewer's language now; the rename edits the Turkish and the English names
                              onClick={() => setRenaming({ id: r.userGroupId, value: r.userGroupNameTr ?? r.userGroupName ?? "", valueEn: r.userGroupNameEn ?? "" })}
                            >
                              <Pencil size={13} />
                            </Button>
                          )}
                          {!r.root && (
                            <span className="text-muted small">{t("yetkiYonetimi.ingilizceAd", { ad: r.userGroupNameEn || "—" })}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td><Badge color={kind.color} pill>{kind.label}</Badge></td>
                    {/* WAS (until #408): the info cell of "Sayfa erişimi": "Tümü (kök)" for root, "N sayfa yetkisi" for a staff role (the count of its
                        page.* permissions in the matrix), "Firma portalı" for the others (until #325 a <SayfaErisimiHucresi> select stood here). */}
                    <td className="text-center">
                      <Button size="sm" color="link" className="p-0" title={t("yetkiYonetimi.uyeleriGoster")} onClick={() => onShowMembers(r.userGroupId)}>
                        {r.activeUsers}
                      </Button>
                    </td>
                    {permissions.map((p) => {
                      // #308: kök olmayan yönetici kendi rolünü ve rbac.manage'i değiştiremez (backend 403).
                      const locked = !me?.root
                        && (r.userGroupId === me?.userGroupId || p.permissionKey === PERMISSION_KEYS.RBAC_MANAGE);
                      return (
                        <td key={p.permissionId} className="text-center">
                          {r.allPermissions ? (
                            <Badge color="light" className="text-muted border" title={t("yetkiYonetimi.superAdminHerYetkiyeKoddaSahip")}>{t("yetkiYonetimi.tumu")}</Badge>
                          ) : !sideMatches(r, p) ? (
                            <span className="text-muted" title={kapsamOf(p) === KAPSAM.PORTAL
                              ? t("yetkiYonetimi.firmaPortaliYetkisiPersonelRolune")
                              : t("yetkiYonetimi.personelYetkisiFirmaTarafindakiRole")}>—</span>
                          ) : (
                            <Input
                              type="checkbox" className="form-check-input"
                              aria-label={`${r.userGroupName}: ${p.permissionName}`}
                              checked={ids.includes(p.permissionId)}
                              // WAS (until #309): disabled={busy === r.userGroupId}
                              disabled={busy === r.userGroupId || locked}
                              title={locked ? t("yetkiYonetimi.bunuYalnizcaKokKullaniciDegistirir") : undefined}
                              onChange={() => toggle(r, p.permissionId)}
                            />
                          )}
                        </td>
                      );
                    })}
                    <td className="text-end pe-3">
                      {!r.allPermissions && (
                        <div className="d-flex gap-1 justify-content-end">
                          {/* #309: bu rolün işlem geçmişi */}
                          <Button size="sm" color="light" className="border" title={t("yetkiYonetimi.islemGecmisi")}
                            onClick={() => onShowHistory?.({ hedefTipi: "ROL", hedefId: r.userGroupId, ad: r.userGroupName })}>
                            <History size={14} />
                          </Button>
                          {dirty && (
                            <Button size="sm" color="light" className="border" title={t("yetkiYonetimi.geriAl")} disabled={busy === r.userGroupId} onClick={() => dropDraft(r.userGroupId)}>
                              <RotateCcw size={14} />
                            </Button>
                          )}
                          <Button size="sm" color="success" disabled={!dirty || busy === r.userGroupId} onClick={() => save(r)}>
                            {busy === r.userGroupId ? <Spinner size="sm" /> : <><Save size={14} className="me-1" />{t("common:kaydet")}</>}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        {/* WAS: "Bir yetki kutusu yalnızca backend'in o yetkiyle koruduğu işlemleri açar; sayfa erişimi hâlâ
            ön yüzdeki rol listelerine bağlı. Satırdaki değişiklik Kaydet'e basılana kadar yazılmaz." */}
        <div className="text-muted small px-3 py-2 border-top">
          {/* WAS (until #325): "Bir yetki kutusu yalnızca backend'in o yetkiyle koruduğu işlemleri açar. Sayfa erişimi,
              rolün kullanıcılarının hangi rolün sayfalarını ve menüsünü göreceğini belirler; değişikliği bir sonraki
              girişlerinde görürler. Satırdaki değişiklik Kaydet'e basılana kadar yazılmaz." */}
          {t("yetkiYonetimi.birYetkiKutusuYalnizcaBackend")}{" "}<b>{t("yetkiYonetimi.sayfa")}</b>{" "}{t("yetkiYonetimi.yetkileri325PersonelPanelininSayfalarini")}
          {/* #308 */}
          {/* WAS (until #419): <b>Portal</b> yetkileri yalnızca firma tarafındaki rollere verilir; bir portal yetkisi bir firma portalı modülünde
              yazma işlemlerini açar, rolde yoksa o modül salt okunur görünür. (7-15 emekli: yerini Portal Rolleri aldı) */}
          {" "}<b>{t("yetkiYonetimi.personel")}</b>{" "}{t("yetkiYonetimi.yetkileriYalnizcaPersonelDenetmenRollerine")}{" "}{t("yetkiYonetimi.firmaTarafiNotu")}
        </div>
      </CardBody>
      {newOpen && (
        <YeniRolModal
          staffRoles={roles.filter(isStaffRole)}
          onClose={() => setNewOpen(false)}
          onCreated={async (created) => {
            setNewOpen(false);
            await onReload();
            // WAS: text: `"${created.userGroupName}" rolü oluşturuldu (#${created.userGroupId}). Henüz hiçbir yetkisi yok.`
            // WAS (until #325): text: "... Sayfaları: ${roleName(created.pageAccessGroupId ?? created.templateGroupId)}. Henüz hiçbir yetkisi yok."
            setMsg({
              color: "success",
              text: t("yetkiYonetimi.roluOlusturuldu", { userGroupName: created.userGroupName, userGroupId: created.userGroupId })
                + t("yetkiYonetimi.rolununSayfaYetkileriKopyalandi", { roleName: roleName(created.templateGroupId), length: (created.sayfaIzinleri || []).length })
                + t("yetkiYonetimi.digerYetkileriBos"),
            });
            onShowMembers(created.userGroupId);
          }}
        />
      )}
    </Card>
  );
}

// #408 (2026-10-14): SayfaErisimiHucresi is GONE with the page-access mechanism (it had not been rendered since #325). WAS, kept commented out:
// /**
//  * #302: one role's "Sayfa erişimi" cell. Only staff roles choose (backend rule); SuperAdmin, company
//  * and company-panel roles always use their own pages. A role others borrow from cannot borrow itself.
//  * #325 (2026-10-11): no longer rendered — pages are permissions now. Kept for the record.
//  */
// // eslint-disable-next-line no-unused-vars
// function SayfaErisimiHucresi({ r, value, sources, borrowers, roleName, disabled, onChange }) {
//   const { t } = useTranslation("ayarlar");
//   if (r.root) return <span className="text-muted small">{t("yetkiYonetimi.tumuKok")}</span>;
//   if (!isStaffRole(r)) return <span className="text-muted small">{t("yetkiYonetimi.kendiSayfalari")}</span>;
//   const lent = borrowers.length > 0;
//   // A stored source missing from the list (should not happen) still shows as the selected option.
//   const options = value && !sources.some((s) => String(s.userGroupId) === value)
//     ? [...sources, { userGroupId: Number(value), userGroupName: roleName(value) }]
//     : sources;
//   return (
//     <>
//       <Input
//         id={`sayfa-erisimi-${r.userGroupId}`}
//         type="select" bsSize="sm" style={{ width: 190 }} aria-label={t("yetkiYonetimi.sayfaErisimi2", { userGroupName: r.userGroupName })}
//         value={value} disabled={disabled || (lent && value === "")}
//         onChange={(e) => onChange(e.target.value)}
//       >
//         <option value="">{t("yetkiYonetimi.kendiSayfalari")}</option>
//         {options.map((s) => <option key={s.userGroupId} value={String(s.userGroupId)}>{s.userGroupName}{" "}{t("yetkiYonetimi.ileAyni")}</option>)}
//       </Input>
//       {lent && value === "" && (
//         <UncontrolledTooltip target={`sayfa-erisimi-${r.userGroupId}`} placement="top">
//           {t("yetkiYonetimi.sablonOlduguRollerOnceOnlarin", { v: borrowers.map((b) => b.userGroupName).join(", ") })}
//         </UncontrolledTooltip>
//       )}
//     </>
//   );
// }

function YeniRolModal({ staffRoles, onClose, onCreated }) {
  const { t } = useTranslation("ayarlar");
  const [name, setName] = useState("");
  const [nameEn, setNameEn] = useState("");   // #421: optional English name
  const [templateId, setTemplateId] = useState(String(staffRoles.find((r) => r.userGroupId === 4)?.userGroupId ?? staffRoles[0]?.userGroupId ?? ""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      // WAS (until #421): const created = await createRole(name.trim(), Number(templateId));
      const created = await createRole(name.trim(), Number(templateId), nameEn.trim() || undefined);
      await onCreated(created);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  return (
    <Modal isOpen toggle={() => !busy && onClose()} centered>
      <ModalHeader toggle={() => !busy && onClose()}>{t("yetkiYonetimi.yeniRol")}</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        <div className="mb-3">
          <Label for="yeni-rol-adi" className="form-label">{t("yetkiYonetimi.rolAdi")}</Label>
          <Input id="yeni-rol-adi" value={name} maxLength={255} disabled={busy} onChange={(e) => setName(e.target.value)} />
        </div>
        {/* #421: the English name (CLAUDE.md #13); optional, the Turkish name shows in English until one is written */}
        <div className="mb-3">
          <Label for="yeni-rol-adi-en" className="form-label">{t("yetkiYonetimi.rolAdiEn")}</Label>
          <Input id="yeni-rol-adi-en" value={nameEn} maxLength={255} disabled={busy} placeholder={t("yetkiYonetimi.rolAdiEnIpucu")}
                 onChange={(e) => setNameEn(e.target.value)} />
        </div>
        <div className="mb-3">
          <Label for="yeni-rol-sablon" className="form-label">{t("yetkiYonetimi.sablonRol")}</Label>
          <Input id="yeni-rol-sablon" type="select" value={templateId} disabled={busy} onChange={(e) => setTemplateId(e.target.value)}>
            {staffRoles.map((r) => <option key={r.userGroupId} value={r.userGroupId}>{r.userGroupName}</option>)}
          </Input>
          <div className="form-text">
            {t("yetkiYonetimi.yeniRolSablonunPanelAdresini")}
          </div>
        </div>
        {/* WAS (#6): <Alert color="warning">Bu rolün kullanıcıları, ön yüzdeki sayfa erişim listeleri
            (allRoutes.js) bu rolü içerene kadar korumalı sayfaları açamaz.</Alert> */}
        {/* WAS (until #325): "Bu rolün kullanıcıları şablon rolün sayfalarını ve menüsünü görür (sonradan "Sayfa erişimi"
            sütunundan değiştirilebilir). Backend yetkileri ise boş başlar: gereken yetkileri satırdan verin." */}
        <Alert color="info" className="small mb-0">
          {t("yetkiYonetimi.yeniRolSablonun")}{" "}<b>{t("yetkiYonetimi.sayfa")}</b>{" "}{t("yetkiYonetimi.yetkileriniKopyalarYaniAyniSayfalari")}
        </Alert>
      </ModalBody>
      <ModalFooter>
        <Button color="light" disabled={busy} onClick={onClose}>{t("yetkiYonetimi.vazgec")}</Button>
        <Button color="primary" disabled={busy || !name.trim() || !templateId} onClick={submit}>
          {busy ? <Spinner size="sm" /> : t("yetkiYonetimi.olustur")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── 2. Rol Üyeleri ────────────────────────────────────────────────────────────────────
function UyelerKarti({ roles, roleId, onRoleChange, onReload }) {
  const { t: tx } = useTranslation("ayarlar");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [targets, setTargets] = useState({}); // userId -> hedef rol id
  const [busyUser, setBusyUser] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const moveOptions = roles.filter((r) => !r.root && r.userGroupId !== roleId);

  useEffect(() => {
    const t = setTimeout(() => setQuery(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!roleId) return undefined;
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const out = await listRoleMembers(roleId, { q: query });
        if (!ignore) setData(out);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [roleId, query, reloadToken]);

  async function move(u) {
    const target = Number(targets[u.id]);
    if (!target) return;
    setBusyUser(u.id);
    setError(null);
    try {
      // WAS (until #309): await updateUserGroup(u.id, target);
      // #309: the management endpoint (rbac.manage) -- same guards, and the change lands in the history.
      await setManagedUserGroup(u.id, target);
      setTargets((p) => { const n = { ...p }; delete n[u.id]; return n; });
      setReloadToken((n) => n + 1);
      await onReload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyUser(null);
    }
  }

  const role = roles.find((r) => r.userGroupId === roleId);
  return (
    <Card>
      <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
        <UsersIcon size={16} className="text-muted" />
        <h5 className="card-title mb-0 flex-grow-1">{tx("yetkiYonetimi.rolUyeleri")}</h5>
        <Input
          type="select" bsSize="sm" style={{ width: 240 }} aria-label={tx("yetkiYonetimi.rol")}
          value={roleId ?? ""} onChange={(e) => onRoleChange(Number(e.target.value))}
        >
          {roles.map((r) => <option key={r.userGroupId} value={r.userGroupId}>{r.userGroupName} ({r.activeUsers})</option>)}
        </Input>
        <Input
          bsSize="sm" style={{ width: 240 }} placeholder={tx("yetkiYonetimi.adKullaniciAdiVeyaE")} aria-label={tx("yetkiYonetimi.uyeAra")}
          value={q} onChange={(e) => setQ(e.target.value)}
        />
      </CardHeader>
      <CardBody className="p-0">
        {error && <Alert color="danger" className="m-3 mb-0" toggle={() => setError(null)}>{error}</Alert>}
        {loading && !data ? (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        ) : (
          <div className="table-responsive">
            <Table className="align-middle table-nowrap mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-3">{tx("yetkiYonetimi.adSoyad")}</th>
                  <th>{tx("yetkiYonetimi.kullaniciAdi")}</th>
                  <th>{tx("yetkiYonetimi.ePosta")}</th>
                  <th className="text-center">{tx("yetkiYonetimi.durum")}</th>
                  <th>{tx("yetkiYonetimi.sonGiris")}</th>
                  <th className="text-end pe-3" style={{ width: 300 }}>{tx("yetkiYonetimi.baskaRoleTasi")}</th>
                </tr>
              </thead>
              <tbody>
                {(data?.users ?? []).length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted py-4">{loading ? tx("yetkiYonetimi.yukleniyor") : tx("yetkiYonetimi.buRoldeKullaniciYok")}</td></tr>
                )}
                {(data?.users ?? []).map((u) => (
                  <tr key={u.id}>
                    <td className="ps-3 fw-semibold">{[u.userName, u.userSurname].filter(Boolean).join(" ") || "—"}</td>
                    <td className="font-monospace small">{u.userLoginName}</td>
                    <td className="small">{u.userEmail || "—"}</td>
                    <td className="text-center">
                      <Badge color={u.userActive === 1 ? "success" : "secondary"} pill>{u.userActive === 1 ? tx("yetkiYonetimi.aktif") : tx("yetkiYonetimi.pasif")}</Badge>
                    </td>
                    <td className="small text-muted">{fmtDate(u.userLastLogin)}</td>
                    <td className="text-end pe-3">
                      {role?.root ? (
                        <span className="text-muted small">{tx("yetkiYonetimi.kokYalnizcaKokDevriIle")}</span>
                      ) : (
                        <div className="d-flex gap-1 justify-content-end">
                          <Input
                            type="select" bsSize="sm" style={{ width: 200 }} aria-label={tx("yetkiYonetimi.icinHedefRol", { userLoginName: u.userLoginName })}
                            value={targets[u.id] ?? ""} disabled={busyUser === u.id}
                            onChange={(e) => setTargets((p) => ({ ...p, [u.id]: e.target.value }))}
                          >
                            <option value="">{tx("yetkiYonetimi.rolSecin")}</option>
                            {moveOptions.map((r) => <option key={r.userGroupId} value={r.userGroupId}>{r.userGroupName}</option>)}
                          </Input>
                          <Button size="sm" color="primary" disabled={!targets[u.id] || busyUser === u.id} onClick={() => move(u)}>
                            {busyUser === u.id ? <Spinner size="sm" /> : tx("yetkiYonetimi.tasi")}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {data && (
          <div className="text-muted small px-3 py-2 border-top">
            {data.total > data.users.length
              ? tx("yetkiYonetimi.kullanicidanIlkTanesiGosteriliyorAramayla", { total: data.total, length: data.users.length })
              : tx("yetkiYonetimi.kullanici", { total: data.total })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ─── 3. Kök Devri ──────────────────────────────────────────────────────────────────────
function KokDevriKarti({ roles }) {
  const { t } = useTranslation("ayarlar");
  const me = Number(localStorage.getItem("userId"));
  const staffRoles = useMemo(() => roles.filter(isStaffRole), [roles]);
  const staffRoleIds = useMemo(() => new Set(staffRoles.map((r) => r.userGroupId)), [staffRoles]);
  const [candidates, setCandidates] = useState([]);
  const [q, setQ] = useState("");
  const [targetId, setTargetId] = useState("");
  const [myNewRole, setMyNewRole] = useState(String(staffRoles.find((r) => r.userGroupId === 2)?.userGroupId ?? ""));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const rows = await getStaffUsers();
        if (!ignore) setCandidates(rows || []);
      } catch (e) {
        if (!ignore) setError(e.message);
      }
    }
    run();
    return () => { ignore = true; };
  }, []);

  // Aday: aktif, ben değilim, personel rolünde (backend aynısını şart koşuyor).
  const eligible = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase("tr-TR");
    return candidates
      .filter((u) => u.userActive === 1 && u.id !== me && staffRoleIds.has(u.userGroupId))
      .filter((u) => !needle || [u.userName, u.userSurname, u.userLoginName, u.userEmail]
        .filter(Boolean).join(" ").toLocaleLowerCase("tr-TR").includes(needle))
      .slice(0, 100);
  }, [candidates, q, me, staffRoleIds]);
  const target = candidates.find((u) => String(u.id) === String(targetId));

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await transferRoot(Number(targetId), Number(myNewRole));
      setDone(true);
      setConfirmOpen(false);
      setTimeout(() => { window.location.href = "/Dashboard"; }, 3000);
    } catch (e) {
      setError(e.message);
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="border border-danger">
      <CardHeader className="d-flex align-items-center gap-2 bg-danger-subtle">
        <KeyRound size={16} className="text-danger" />
        <h5 className="card-title mb-0 text-danger">{t("yetkiYonetimi.kokDevri")}</h5>
      </CardHeader>
      <CardBody>
        {done ? (
          <Alert color="success" className="mb-0">
            {t("yetkiYonetimi.kokKullanici")}{" "}{target ? `${target.userName ?? ""} ${target.userSurname ?? ""}`.trim() : ""}{" "}{t("yetkiYonetimi.hesabinaDevredildiBuSayfaArtik")}
          </Alert>
        ) : (
          <>
            <p className="text-muted mb-3">
              {t("yetkiYonetimi.sistemdeTekBirKokKullanici")}
            </p>
            {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
            <Row className="g-3">
              <Col md={6}>
                <Label for="kok-aday-ara" className="form-label">{t("yetkiYonetimi.yeniKokKullanici")}</Label>
                <Input id="kok-aday-ara" bsSize="sm" className="mb-2" placeholder={t("yetkiYonetimi.adKullaniciAdiVeyaE2")} value={q} onChange={(e) => setQ(e.target.value)} />
                <Input id="kok-aday" type="select" bsSize="sm" aria-label={t("yetkiYonetimi.yeniKokKullanici")} value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                  <option value="">{t("yetkiYonetimi.kullaniciSecin")}</option>
                  {eligible.map((u) => (
                    <option key={u.id} value={u.id}>
                      {[u.userName, u.userSurname].filter(Boolean).join(" ") || u.userLoginName} — {u.userLoginName} ({u.userGroupName})
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={6}>
                <Label for="kok-yeni-rolum" className="form-label">{t("yetkiYonetimi.devirdenSonraSizinRolunuz")}</Label>
                <Input id="kok-yeni-rolum" type="select" bsSize="sm" value={myNewRole} onChange={(e) => setMyNewRole(e.target.value)}>
                  <option value="">{t("yetkiYonetimi.rolSecin")}</option>
                  {staffRoles.map((r) => <option key={r.userGroupId} value={r.userGroupId}>{r.userGroupName}</option>)}
                </Input>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-3">
              <Button color="danger" disabled={!targetId || !myNewRole} onClick={() => { setTyped(""); setConfirmOpen(true); }}>
                {t("yetkiYonetimi.kokuDevret")}
              </Button>
            </div>
          </>
        )}
      </CardBody>
      <Modal isOpen={confirmOpen} toggle={() => !busy && setConfirmOpen(false)} centered>
        <ModalHeader toggle={() => !busy && setConfirmOpen(false)}>{t("yetkiYonetimi.kokKullaniciyiDevret")}</ModalHeader>
        <ModalBody>
          <p>
            <span className="fw-semibold">{target ? [target.userName, target.userSurname].filter(Boolean).join(" ") || target.userLoginName : ""}</span>
            {t("yetkiYonetimi.superAdminOlacakSiz")}{" "}<span className="fw-semibold">{staffRoles.find((r) => String(r.userGroupId) === String(myNewRole))?.userGroupName}</span>{" "}{t("yetkiYonetimi.roluneGececeksinizBuSayfayaBir")}
          </p>
          <Label for="kok-onay" className="form-label">{t("yetkiYonetimi.onaylamakIcin")}{" "}<span className="font-monospace fw-semibold">{TRANSFER_WORD}</span>{" "}{t("yetkiYonetimi.yazin")}</Label>
          <Input id="kok-onay" value={typed} disabled={busy} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={busy} onClick={() => setConfirmOpen(false)}>{t("yetkiYonetimi.vazgec")}</Button>
          <Button color="danger" disabled={busy || typed.trim() !== TRANSFER_WORD} onClick={submit}>
            {busy ? <Spinner size="sm" /> : t("yetkiYonetimi.devret")}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}

// ─── Sayfa ─────────────────────────────────────────────────────────────────────────────
// #309: sekmeler. Kök Devri yalnızca köke görünür (backend de yalnızca kökü kabul ediyor).
const TABS = [
  { key: "kullanicilar", get label() { return i18n.t("ayarlar:yetkiYonetimi.kullanicilar"); } },
  { key: "roller", get label() { return i18n.t("ayarlar:yetkiYonetimi.rollerVeYetkiler"); } },
  { key: "firma", get label() { return i18n.t("ayarlar:yetkiYonetimi.firmaKullanici"); } },
  { key: "gecmis", get label() { return i18n.t("ayarlar:yetkiYonetimi.islemGecmisi2"); } },
  { key: "kok", get label() { return i18n.t("ayarlar:yetkiYonetimi.kokDevri"); }, rootOnly: true },
];

export default function YetkiYonetimiPage() {
  const { t: tx } = useTranslation("ayarlar");
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [memberRoleId, setMemberRoleId] = useState(null);
  // #309
  const [me, setMe] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return TABS.some((x) => x.key === t) ? t : "kullanicilar";
  });
  const [gecmisHedef, setGecmisHedef] = useState(null);

  // #421: role and permission names come in the viewer's language; the lists are fetched again when it changes
  const lang = i18n.language;
  const reload = useCallback(async () => {
    const [p, r] = await Promise.all([listPermissions(), listRoles()]);
    // #308: personel yetkileri önce, portal yetkileri sonra.
    setPermissions([...p].sort((a, b) => (kapsamOf(a) - kapsamOf(b)) || (a.permissionId - b.permissionId)));
    setRoles(r);
    return r;
  }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const [r, who] = await Promise.all([reload(), getMyPermissions({ force: true })]);
        if (!ignore) {
          setMe(who);
          setMemberRoleId((prev) => prev ?? r.find((x) => x.root)?.userGroupId ?? r[0]?.userGroupId ?? null);
        }
      } catch (e) {
        // WAS (until #309): "Yetki Yönetimi yalnızca kök kullanıcıya (SuperAdmin) açık."
        if (!ignore) setError(e.status === 403
          ? i18n.t("ayarlar:yetkiYonetimi.yetkiYonetimiYalnizcaKokKullaniciya")
          : e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
    // WAS (until #421): }, [reload]);
  }, [reload, lang]);

  const showMembers = (id) => {
    setMemberRoleId(id);
    setActiveTab("roller");
    setTimeout(() => document.getElementById("rol-uyeleri")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };
  const showHistory = (hedef) => {
    setGecmisHedef(hedef);
    setActiveTab("gecmis");
  };
  const tabs = TABS.filter((t) => !t.rootOnly || me?.root);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("yetkiYonetimi.yetkiYonetimi")} pageTitle={tx("yetkiYonetimi.sistemAyarlari")} />
        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : error ? (
          <Alert color="danger">{error}</Alert>
        ) : (
          <>
            {/* WAS (until #309): "Bu sayfa yalnızca kök kullanıcıya (SuperAdmin) açık. Burada kaydedilen roller ve
                yetkiler kalıcıdır; hiçbir veritabanı güncellemesi onları sıfırlamaz." */}
            <Alert color="info" className="small">
              {tx("yetkiYonetimi.buSayfaKokKullaniciyaVe")}{" "}<b>{tx("yetkiYonetimi.yetkiYonetimi")}</b>{" "}{tx("yetkiYonetimi.yetkisiOlanPersoneleAcikPersonel")}
              {!me?.root && tx("yetkiYonetimi.kendiRolunuzuVeYetkileriniziDegistiremez")}
            </Alert>
            <Nav pills className="gap-2 mb-3">
              {tabs.map((t) => (
                <NavItem key={t.key}>
                  <NavLink
                    active={activeTab === t.key}
                    className={activeTab === t.key ? "bg-danger text-white" : "bg-white text-dark border"}
                    style={{ cursor: "pointer" }}
                    onClick={() => setActiveTab(t.key)}
                  >
                    {t.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
            {activeTab === "kullanicilar" && (
              <YetkiKullanicilarSekmesi roles={roles} me={me} onReload={reload} onShowHistory={showHistory} />
            )}
            {activeTab === "roller" && (
              <>
                {/* #419: the retired company-side permissions (7-15) are not columns any more; the history below still names them */}
                {/* WAS (until #419): <RollerKarti roles={roles} permissions={permissions} ... /> */}
                <RollerKarti roles={roles} permissions={permissions.filter((p) => !p.retired)} onReload={reload} onShowMembers={showMembers}
                  onShowHistory={showHistory} me={me} />
                <div id="rol-uyeleri">
                  <UyelerKarti roles={roles} roleId={memberRoleId} onRoleChange={setMemberRoleId} onReload={reload} />
                </div>
              </>
            )}
            {activeTab === "firma" && <YetkiFirmaKullaniciSekmesi me={me} onReload={reload} />}
            {activeTab === "gecmis" && (
              <YetkiGecmisSekmesi roles={roles} permissions={permissions} hedef={gecmisHedef}
                onClearHedef={() => setGecmisHedef(null)} />
            )}
            {activeTab === "kok" && me?.root && <KokDevriKarti roles={roles} />}
          </>
        )}
      </Container>
    </div>
  );
}
